import { useCallback, useEffect } from 'react';
import { useIntl } from 'react-intl';
import {
    RecoilValue,
    Snapshot,
    useRecoilCallback,
    useRecoilValue,
    useSetRecoilState,
} from 'recoil';
import { useSessionOrNull } from '~/contexts/SessionContext';
import {
    domainEventBus,
    ROI_CONTOUR_EDIT_EVENT_TYPES,
    RoiContourEditEvent,
    RoiPropertiesUpdatedEvent,
} from '~/domain/events';
import type { PointMm } from '~/domain/geometry/patientGeometry';
import { findAxialDiameters } from '~/domain/measurement/axialDiameters/axialDiametersSearch';
import { OutlineTooComplexError } from '~/domain/measurement/axialDiameters/longestInsideChord';
import { createDistanceMeasurement } from '~/domain/measurement/measurement';
import type {
    RoiReadModel,
    StructureSetReadModel,
} from '~/domain/readmodels/StructureSetReadModel';
import { structureSdfOf } from '~/domain/structures/structureSdf';
import type { Grid } from '~/engine/data/common/Grid';
import type { ImageObject } from '~/engine/data/image/ImageObject';
import { getDatasetEnvironmentRegistry } from '~/engine/scene/datasetEnvironmentRegistryInstance';
import {
    ACQUIRED_SLICE_AXIS,
    annotationPlaneThrough,
    viewShowsPlane,
} from '~/engine/view/planeViewGeometry';
import type { PlaneView } from '~/engine/view/views/PlaneView';
import { Orientations } from '~/engine/view/viewOrientation';
import { useEnvironmentViews } from '~/hooks/environment/useEnvironmentViews';
import {
    resolveActiveEnvironment,
    useActiveEditTarget,
} from '~/hooks/target/useActiveEditTarget';
import { useDomainVersion } from '~/hooks/domain/useDomainVersion';
import { goToAnchor } from '~/hooks/useAnnotationGoTo';
import useBusyIndicator from '~/hooks/useBusyIndicator';
import type { CatalogMessageId } from '~/i18n';
import { viewManagerState } from '~/state/atoms/engineAtoms';
import {
    MeasuredAxialDiameters,
    measuredAxialDiametersState,
} from '~/state/atoms/measurementAtoms';
import { selectedRoiReadModelState } from '~/state/atoms/structureSet/roiAtoms';
import { currentStructureSetState } from '~/state/atoms/structureSet/structureSetAtoms';
import { enqueueSnackbar } from '~/utils/notifications';
import { waitForUI } from '~/utils/uiUtils';

/** Slice planes of a field and an image closer than this, in mm, are one plane. */
const SLICE_COINCIDENCE_TOLERANCE_MM = 0.01;

/** From this many voxels the search may take over 150 ms: the busy indicator is painted first. */
export const BUSY_INDICATOR_MIN_VOXELS = 8_000_000;

/**
 * Whether every slice of `field` lies on a slice of `image`, each on the
 * next: checked at the corners of its first and last slices.
 */
export function fieldSlicesAreImageSlices(field: Grid, image: Grid): boolean {
    // n on the centre plane of image slice n.
    const imageSliceAt = (x: number, y: number, z: number) =>
        image.toVoxelSpace(field.toWorldSpace([x, y, z + 0.5]))[2] - 0.5;
    const first = Math.round(imageSliceAt(0, 0, 0));
    return [0, field.sizeX].every((x) =>
        [0, field.sizeY].every((y) =>
            [0, field.sizeZ - 1].every(
                (z) =>
                    Math.abs(imageSliceAt(x, y, z) - (first + z)) *
                        image.resZ <=
                    SLICE_COINCIDENCE_TOLERANCE_MM
            )
        )
    );
}

const readerOf =
    (snapshot: Snapshot) =>
    <T>(state: RecoilValue<T>): T =>
        snapshot.getLoadable(state).getValue();

type RoiKey = Pick<RoiReadModel, 'roiId' | 'structureSetUid'>;

const isSameRoi = (a: RoiKey | null | undefined, b: RoiKey | undefined) =>
    !!a && a.roiId === b?.roiId && a.structureSetUid === b.structureSetUid;

/**
 * The structure, its image and its Environment as the viewer holds them, read
 * only through the structure's own set.
 */
function heldStructure(
    key: RoiKey,
    activeSet: StructureSetReadModel | undefined
) {
    const environment = resolveActiveEnvironment(
        getDatasetEnvironmentRegistry(),
        activeSet
    );
    const image = environment?.image;
    const roi =
        activeSet?.dicomUid === key.structureSetUid &&
        environment?.structureSet?.parent.id === activeSet.id
            ? environment.structureSet.getRoi(key.roiId)
            : undefined;
    return environment && image && roi
        ? { environment, image, roi }
        : undefined;
}

const transversalPlaneThrough = (image: ImageObject, pointMm: PointMm) =>
    annotationPlaneThrough(
        image.parent.seriesUid,
        Orientations.TRANSVERSAL_ORIENTATION,
        image.grid,
        pointMm
    );

/** Why `roi` cannot be measured, if it cannot; its slices once `field` is built. */
function refusalOf(
    roi: Pick<RoiReadModel, 'isPointRoi'>,
    held: ReturnType<typeof heldStructure>,
    measured: boolean,
    views: readonly PlaneView[],
    field?: Grid
): CatalogMessageId | undefined {
    if (roi.isPointRoi) {
        return 'structure.measureAxialDiameters.refusal.pointRoi';
    }
    if (!held) {
        return 'structure.measureAxialDiameters.refusal.imageNotShown';
    }
    if (held.roi.isEmpty) {
        return 'structure.measureAxialDiameters.refusal.empty';
    }
    if (measured) {
        return 'structure.measureAxialDiameters.refusal.alreadyMeasured';
    }
    const { grid } = held.image;
    const plane = transversalPlaneThrough(held.image, [0, 0, 0]);
    if (!views.some((view) => viewShowsPlane(view, plane))) {
        return 'structure.measureAxialDiameters.refusal.imageNotShown';
    }
    return grid.axisAlong(plane.viewDirection) !== ACQUIRED_SLICE_AXIS ||
        (field && !fieldSlicesAreImageSlices(field, grid))
        ? 'structure.measureAxialDiameters.refusal.slicesNotShown'
        : undefined;
}

/** Whether both diameter rulers of `roi` are shown. */
const pairShown = (measured: readonly MeasuredAxialDiameters[], roi: RoiKey) =>
    measured.some((pair) => isSameRoi(pair, roi) && pair.rulers.length === 2);

/** `measured` without the pair of `roi`; the same array when it holds none. */
const withoutPairOf =
    (roi: RoiKey) => (measured: readonly MeasuredAxialDiameters[]) =>
        measured.some((pair) => isSameRoi(pair, roi))
            ? measured.filter((pair) => !isSameRoi(pair, roi))
            : measured;

/**
 * Removes a structure's measured diameters once its contours change, by an
 * edit, an undo or a redo, or once it is deleted or overwritten by a copy, or
 * its structure set is deleted: the rulers would no longer measure it.
 * Selecting another structure, or none, keeps them. Mounted once, by the
 * viewer, so it acts whichever controls are open.
 */
export function useWithdrawAxialDiameters(): void {
    const setMeasured = useSetRecoilState(measuredAxialDiametersState);
    const session = useSessionOrNull();
    const domainVersion = useDomainVersion();
    // Re-checked on every changeset: a structure set goes, and a copy
    // overwrites a structure, without the events subscribed to below.
    useEffect(() => {
        const roisOf = new Map(
            session?.patient.structureSets.map(({ dicomUid, rois }) => [
                dicomUid,
                new Set(rois.map(({ id }) => id)),
            ])
        );
        const held = ({ structureSetUid, roiId }: MeasuredAxialDiameters) =>
            roisOf.get(structureSetUid)?.has(roiId) ?? false;
        setMeasured((measured) =>
            measured.every(held) ? measured : measured.filter(held)
        );
    }, [session, domainVersion, setMeasured]);
    useEffect(() => {
        const onContourEdit = (event: RoiContourEditEvent) =>
            setMeasured(withoutPairOf(event));
        const onPropertiesUpdated = (event: RoiPropertiesUpdatedEvent) => {
            if (event.updatedProperties.deleted) {
                setMeasured(withoutPairOf(event));
            }
        };
        const subscriptions = [
            ...ROI_CONTOUR_EDIT_EVENT_TYPES.map((eventType) =>
                domainEventBus.subscribe(eventType, onContourEdit)
            ),
            domainEventBus.subscribe(
                RoiPropertiesUpdatedEvent.EVENT_TYPE,
                onPropertiesUpdated
            ),
        ];
        return () => {
            subscriptions.forEach((subscription) => subscription.unsubscribe());
        };
    }, [setMeasured]);
}

/**
 * Measures the long and short diameters of `roi`, the selected structure, on
 * one transversal slice as two distances drawn on the image, the long one
 * first, with the crosshair where they cross; they stay beside the diameters
 * of other structures. Refused while both are shown, and with one left,
 * replacing it, so a structure's are only ever shown as a pair.
 * `refusalMessageId` says why it cannot run; failures go to a snackbar.
 */
export function useMeasureAxialDiameters(roi: RoiReadModel) {
    const intl = useIntl();
    const { showBusy, hideBusy } = useBusyIndicator();
    const { structureSet } = useActiveEditTarget();
    const measured = useRecoilValue(measuredAxialDiametersState);

    const held = heldStructure(roi, structureSet);
    const views = useEnvironmentViews(held?.environment);
    const refusalMessageId = refusalOf(
        roi,
        held,
        pairShown(measured, roi),
        views,
        held?.roi.builtTexture?.textureGrid
    );

    /** Measures the viewer as it is at the call; returns the warning to give. */
    const measureNow = useRecoilCallback(
        (callback) => (): CatalogMessageId | undefined => {
            const read = readerOf(callback.snapshot);
            // A run the selection outlived stops.
            if (!isSameRoi(read(selectedRoiReadModelState), roi)) {
                return undefined;
            }
            const viewManager = read(viewManagerState);
            const structure = heldStructure(
                roi,
                read(currentStructureSetState)
            );
            const measured = pairShown(read(measuredAxialDiametersState), roi);
            const views = structure
                ? (viewManager?.viewsOf(structure.environment) ?? [])
                : [];
            const refusal = (field?: Grid) =>
                refusalOf(roi, structure, measured, views, field);
            // Built only when nothing else refuses; read only once its slices match.
            const texture = refusal() ? undefined : structure?.roi.texture;
            const refused = refusal(texture?.textureGrid);
            if (refused) {
                return refused;
            }
            const sdf = structureSdfOf(texture);
            if (!structure || !sdf) {
                throw new Error('The structure has no field to measure.');
            }
            const result = findAxialDiameters(sdf);
            if (!result) {
                return 'structure.measureAxialDiameters.nothingMeasured';
            }
            const { centreMm } = result;
            const { image } = structure;
            const imageSlice = image.grid.toVoxelSpace(centreMm)[2];
            if (!(imageSlice >= 0 && imageSlice < image.grid.sizeZ)) {
                throw new Error('The measured slice lies outside the image.');
            }
            const plane = transversalPlaneThrough(image, centreMm);
            const rulers = [result.long, result.short].map(({ points }) =>
                createDistanceMeasurement(plane, ...points)
            );
            // Crosshair first: nothing is drawn when the go-to cannot land.
            if (goToAnchor(viewManager, rulers[0], centreMm)) {
                callback.set(measuredAxialDiametersState, (measured) => [
                    ...withoutPairOf(roi)(measured),
                    {
                        roiId: roi.roiId,
                        structureSetUid: roi.structureSetUid,
                        rulers,
                    },
                ]);
            }
            return undefined;
        },
        [roi]
    );

    const measureAxialDiameters = useCallback(async (): Promise<void> => {
        const text = (id: string) => intl.formatMessage({ id });
        const say = (id: string, variant: 'warning' | 'error' = 'warning') =>
            enqueueSnackbar(text(id), { variant });
        // A field not built yet is at most as large as its image.
        const grid = held?.roi.builtTexture?.textureGrid ?? held?.image.grid;
        const busy =
            !!grid &&
            !roi.isPointRoi &&
            grid.sizeX * grid.sizeY * grid.sizeZ >= BUSY_INDICATOR_MIN_VOXELS;
        try {
            if (busy) {
                await showBusy(text('structure.measureAxialDiameters.busy'));
                await waitForUI();
            }
            const warning = measureNow();
            if (warning) {
                say(warning);
            }
        } catch (error) {
            if (error instanceof OutlineTooComplexError) {
                say('structure.measureAxialDiameters.tooComplex');
            } else {
                console.error('Measure axial diameters error:', error);
                say('structure.measureAxialDiameters.failed', 'error');
            }
        } finally {
            if (busy) {
                hideBusy();
            }
        }
    }, [held, hideBusy, intl, measureNow, roi, showBusy]);

    return { refusalMessageId, measureAxialDiameters };
}
